"use client";
import useContentAutoResume from "@/hooks/useContentAutoResume";
import React from "react";
import CustomButton from "@/components/common/button";
import Cards from "@/components/content/cards";
import ContentSuggestionsRail from "@/components/content/ContentSuggestionsRail";
import Comments from "@/components/content/comments";
import PreviewModal from "@/components/content/previewModal";
import DetailHeroScene from "@/components/common/DetailHeroScene";
import { getPathname } from "@/helpers/browser.utils";
import { useRouter, useSearchParams } from "next/navigation";
import { ROUTES } from "@/routes/appRoutes";
import { useSelector } from "react-redux";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import { mergeArtistDuplicates } from "@/helpers/data.utils";
import { PlayIcon } from "@vidstack/react/icons";
import ContentActions from "@/components/content/contentActions";
import UserReport from "../user-report";
import { useContentViewAnalytics } from "@/hooks/useMediaAnalytics";
import {
  buildContentAnalyticsPayload,
  sendPlayIntent,
} from "@/services/mediaAnalyticsService";
import NextPlayGuideHelper from "@/helpers/nextPlayGuideHelper";
import { isComingSoon } from "@/utils/contentStatus";

const MusicDetail = ({ data, isAdminPreview = false }) => {
  const router = useRouter();
  const prepareAutoResume = useContentAutoResume(data?.slug);
  const searchParams = useSearchParams();
  useContentViewAnalytics(data, { enabled: !isAdminPreview });
  const { isLogin } = useSelector((state) => state.Auth?.profile);
  const { canPlay } = useSubscriptionStatus();
  const entrypointGuide =
    NextPlayGuideHelper.getNextPlayGuideFromSearchParams(searchParams);

  data = data || { deleted: true };

  const { artists } = data;

  const artistList = artists?.reduce((acc, i) => {
    const { farsiName, image, _id, slug } = i.artist?.meta?.bin || {};
    return acc.concat({
      artist: {
        farsiName,
        profession: i.profession?.name,
        contentPhotos: [{ type: "banner", src: image }],
        id: _id,
        slug,
        priority: i.priority ?? i.artist?.priority,
      },
    });
  }, []);

  const reportContentWatch = async () => {
    try {
      await axiosInstance.post(getCMSApiRoute("action/watch"), {
        contentKey: data.slug,
        isEpisode: 0,
      });
    } catch (error) {
      console.log(error);
    }
  };

  if (!data) {
    return (
      <div className="min-h-[100vh] flex justify-center items-center">
        <Card className="dark p-10">
          <CardBody>محتوای مورد نظر یافت نشد</CardBody>
        </Card>
      </div>
    );
  }

  data?.genreInfo?.forEach((item) => {
    item.isGenre = true;
  });

  data?.categoryInfo?.forEach((item) => {
    item.isGenre = false;
  });

  const getPlayUrl = () => {
    const playerUrl = ROUTES.player + "/" + data.slug;
    return NextPlayGuideHelper.addNextPlayGuideToUrl(
      playerUrl,
      entrypointGuide
    );
  };

  const reportPlayIntent = () => {
    const payload = buildContentAnalyticsPayload(data, {
      surface: "content_detail",
      source: "primary_play_button",
    });
    if (payload) sendPlayIntent(payload);
  };

  const handleWatchEvent = async (data) => {
    if (isComingSoon(data?.status)) return;
    try {
      if (data.isFree === 1) {
        if (!isLogin) {
          return router.push(
            `/auth/login?ref=${encodeURIComponent(getPlayUrl())}`
          );
        } else {
          const playUrl = getPlayUrl();
          await reportContentWatch();
          reportPlayIntent();
          prepareAutoResume();
          router.push(playUrl);
        }
      } else {
        if (!isLogin) {
          return router.push(
            `/auth/login?ref=${encodeURIComponent(getPathname())}`
          );
        } else {
          if (!canPlay) {
            return router.push("/subscription");
          } else {
            const playUrl = getPlayUrl();
            await reportContentWatch();
            reportPlayIntent();
            prepareAutoResume();
            router.push(playUrl);
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getWatchLabel = () => {
    if (!isLogin) {
      return "ورود و تماشا";
    } else {
      if (data.isFree === 1) {
        return "تماشا کن";
      } else if (data.isFree !== 1 && canPlay) {
        return "تماشا کن";
      } else {
        return "خرید اشتراک و تماشا";
      }
    }
  };

  return (
    <div className="content-detail-page h-auto min-h-screen">
      <DetailHeroScene
        content={{
          ...data,
          titleEn: data.titleEN,
        }}
        renderButtons={() => (
          <>
            {isComingSoon(data?.status) ? (
              <span className="inline-flex min-h-11 items-center rounded-full border border-white/30 bg-white/10 px-5 font-semibold text-white">
                فعلاً قابل تماشا نیست
              </span>
            ) : (
              <CustomButton
                label={getWatchLabel()}
                color="primary"
                textColor="text-primary-white"
                classNames="content-detail-play-button"
                rightIcon={<PlayIcon />}
                onClick={() => handleWatchEvent(data)}
                size="lg"
              />
            )}
            <PreviewModal src={data.trailer} />
            <ContentActions contentKey={data.slug} />
            <UserReport contentKey={data.slug} data={data} />
          </>
        )}
      />
      {artistList?.length > 0 && (
        <section className="detail-artist-section lg:py-20 md:py-12 py-6 md:container mx-auto mb-5 relative z-10 px-5 2xl:px-0 xlg:max-w-[1920px]">
          <Cards
            items={mergeArtistDuplicates(artistList)}
            shape="square"
            isArtist={true}
            isSwiper={true}
            noTag={true}
            categoryName="هنرمندان"
            showTitle={true}
            type="artist"
            noContainer={true}
          />
        </section>
      )}
      <ContentSuggestionsRail
        slug={data.slug}
        type={data?.contentType?.kind || "movie"}
        enabled={!isAdminPreview}
      />
      <div className="mt-10 container mx-auto">
        <Comments contentKey={data.slug} />
      </div>
    </div>
  );
};

export default MusicDetail;
